import { computed, inject, Injectable, signal } from '@angular/core';
import { Auth, confirmPasswordReset, signInWithCustomToken } from '@angular/fire/auth';
import { checkActionCode, signOut, User } from 'firebase/auth';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthStatus } from '../interfaces/auth-status.enum';
import { catchError, forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { LoginResponse } from '../interfaces/login-response.interface';
import { Router } from '@angular/router';
import { ToastService } from '../../../utils/toast.service';
import { decrypt, encrypt, obfuscateKey } from 'src/ui/types/crypto';
import { FilterCacheService } from 'src/core/services/filter-cache.service';



@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly base = environment.baseURL;
  private readonly http = inject(HttpClient);
  private readonly _auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly key = environment.encryptionKey;
  private readonly profilesAccess = ['Administrador', 'Monitoreo'];

  private readonly LS = {
    token: obfuscateKey('access_token', this.key),
    profile: obfuscateKey('user_profile', this.key),
    expires: obfuscateKey('token_expires_at', this.key),
    email: obfuscateKey('user_email', this.key),
  };

  private readonly _accessToken = signal<string | null>(
    (() => {
      const raw = localStorage.getItem(this.LS.token);
      if (!raw) return null;
      try {
        return decrypt(raw, this.key);
      } catch {
        localStorage.removeItem(this.LS.token);
        return null;
      }
    })()
  );

  private readonly _userProfile = signal<any | null>(
    (() => {
      const raw = localStorage.getItem(this.LS.profile);
      if (!raw) return null;
      try {
        return JSON.parse(decrypt(raw, this.key));
      } catch {
        localStorage.removeItem(this.LS.profile);
        return null;
      }
    })()
  );

  private readonly _userEmail = signal<string | null>(
    (() => {
      const raw = localStorage.getItem(this.LS.email);
      if (!raw) return null;
      try {
        return decrypt(raw, this.key);
      } catch {
        localStorage.removeItem(this.LS.email);
        return null;
      }
    })()
  );


  private readonly _authStatus = signal<AuthStatus>(
    this._accessToken() ? AuthStatus.authenticated : AuthStatus.notAuthenticated
  );

  public authStatus = computed(() => this._authStatus());
  public accessToken = computed(() => this._accessToken());
  public userProfile = computed(() => this._userProfile());
  public userName = computed(() => this._userProfile()?.name ?? '');
  public userId = computed(() => this._userProfile()?.id ?? '');
  public userProfileName = computed(() => this._userProfile()?.profile?.name ?? '');
  public userApprovalNumber = computed(() => this._userProfile()?.approval_number ?? '');
  public userEmail = computed(() => this._userEmail() ?? '');

  constructor() {
    const expires = this.getTokenExpiration();
    const profile = this._userProfile();
    if (this._accessToken() && Date.now() < expires && profile) {
      this._authStatus.set(AuthStatus.authenticated);
      this.setupAutoLogout();
    } else {
      this.cleanup();
    }
  }

  private getTokenExpiration(): number {
    const expRaw = localStorage.getItem(this.LS.expires);
    if (!expRaw) return 0;
    try {
      return parseInt(decrypt(expRaw, this.key), 10);
    } catch {
      localStorage.removeItem(this.LS.expires);
      return 0;
    }
  }

  private setupAutoLogout(): void {
    const expires = this.getTokenExpiration();
    const timeout = expires - Date.now();
    if (timeout > 0) {
      setTimeout(() => {
        this.toast.showToast(
          'fi fi-ss-diamond-exclamation text-[24px] text-[#FF3030]',
          'Tu sesión ha expirado por inactividad',
          'error'
        );
        this.signOut().subscribe({
          next: () => this.router.navigateByUrl('/login'),
          error: () => this.router.navigateByUrl('/login'),
        });
      }, timeout);
    }
  }

  async refreshFirebaseToken(): Promise<string> {
    const user: User | null = this._auth.currentUser;
    if (!user) return this._accessToken() ?? '';
    const newToken = await user.getIdToken(true);
    if (newToken) {
      this._accessToken.set(newToken);
      localStorage.setItem(this.LS.token, encrypt(newToken, this.key));
      const expiresAt = Date.now() + 2 * 60 * 60 * 1000;
      localStorage.setItem(this.LS.expires, encrypt(expiresAt.toString(), this.key));
      return newToken;
    }
    return this._accessToken() ?? '';
  }

  private readonly filterCache = inject(FilterCacheService);

  signInUnified(creds: { email: string; password: string }): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.base}/api/auth/user/get-token`, creds)
      .pipe(
        switchMap(res => {
          if (res.statusCode === 201 && res.data) {
            const token = res.data.access_token;
            const expiresAt = Date.now() + 2 * 60 * 60 * 1000;

            this._accessToken.set(token);
            localStorage.setItem(this.LS.token, encrypt(token, this.key));
            localStorage.setItem(this.LS.expires, encrypt(expiresAt.toString(), this.key));

            // Limpia los filtros de todos los módulos relevantes
            this.filterCache.clearFilters('glassBreak');
            this.filterCache.clearFilters('boxOpening');
            this.filterCache.clearFilters('dispatchForm');

            return from(signInWithCustomToken(this._auth, res.data.custom_token)).pipe(
              switchMap(() => this.getUserProfile(creds.email)),
              map(profile => {
                const profileName = profile?.profile?.name ?? '';
                // Si no está activo o no tiene perfil permitido, lanzar error de permisos
                if (!profile || profile.state !== 'active' || !this.profilesAccess.includes(profileName)) {
                  throw new Error('NO_PERMISSIONS');
                }
                this._authStatus.set(AuthStatus.authenticated);
                this.setupAutoLogout();
                return res;
              })
            );
          }
          throw new Error('Credenciales inválidas');
        })
      );
  }

  getUserProfile(email: string): Observable<any> {
    return this.http
      .post<{
        statusCode: number;
        message: string;
        data: { items: any[]; total: number };
      }>(
        `${this.base}/back/user/search`,
        { email }
      )
      .pipe(
        map(resp => resp.data.items[0] ?? null),
        switchMap(user => {
          if (user) {
            // Guarda SIEMPRE el perfil antes de cualquier validación
            this._userProfile.set(user);
            this._userEmail.set(user.email);
            const json = JSON.stringify(user);
            localStorage.setItem(this.LS.profile, encrypt(json, this.key));
            localStorage.setItem(this.LS.email, encrypt(user.email, this.key));

            const profileName = user.profile?.name ?? '';
            if (!this.profilesAccess.includes(profileName)) {
              // Cierra sesión y lanza error para que el componente lo maneje
              return this.signOut().pipe(
                map(() => { throw new Error('NO_PERMISSIONS'); })
              );
            }
          }
          return of(user);
        })
      );
  }

  signOut(): Observable<void> {
    // Obtén el uid ANTES de limpiar cualquier estado
    const uid = this._userProfile()?.id ?? '';

    const backendLogout$ = uid
      ? this.http
          .post<void>(`${this.base}/back/user-session/logout`, {
            user_id: uid,
            application_type: 'WEB',
          })
          .pipe(catchError(() => of(void 0)))
      : of(void 0);

    const fbLogout$ = from(signOut(this._auth));

    // Primero backend, luego Firebase, luego cleanup
    return backendLogout$.pipe(
      switchMap(() => fbLogout$),
      tap(() => this.cleanup()),
      map(() => undefined)
    );
  }

  private cleanup(): void {
    this._accessToken.set(null);
    this._userProfile.set(null);
    this._userEmail.set(null);
    this._authStatus.set(AuthStatus.notAuthenticated);
    Object.values(this.LS).forEach(k => localStorage.removeItem(k));
  }

  async forgotPassword(email: string) {
    const res = await fetch(`${this.base}/api/auth/user/send-reset-password`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email })
    });
    if (!res.ok) throw new Error(`Error: ${res.status}`);
    return res.json();
  }

  async confirmResetPassword(code: string, newPassword: string) {
    await confirmPasswordReset(this._auth, code, newPassword);
    await signOut(this._auth);
    this.cleanup();
  }

  async isTokenExpired(code: string | null): Promise<boolean> {
    try { await checkActionCode(this._auth, code!); return false; }
    catch { return true; }
  }
}
